import { useQueryClient } from "@tanstack/react-query"
import { useCallback, useEffect, useSyncExternalStore } from "react"
import { useAuth, type HqUser } from "./auth"
import { saveMyMetadata } from "./account-metadata"
import { NAV } from "./nav"
import { ROLE_FAVOURITES, useMyRole } from "./roles"

/**
 * The sections a person stars on More (owners, 14 Oct 2026: "we have so many"), shown at the top of
 * More and as quick links at the bottom of Today. Like the language (lib/language.ts) they follow the
 * PERSON: kept on their Medusa account (user metadata `hq_favourites` + `hq_favourites_at`) and on the
 * device, the newer choice winning, so a star set on the phone shows on the laptop too.
 */

const KEY = "hq-favourites"
type Saved = { paths: string[]; at: number }

/** Only sections that exist, once each, in the order they were starred. Today is home already. */
const STARRABLE = new Set(NAV.map((item) => item.to).filter((to) => to !== "/"))
export const cleanFavourites = (paths: unknown): string[] =>
  Array.isArray(paths) ? [...new Set(paths.filter((path): path is string => typeof path === "string" && STARRABLE.has(path)))] : []

function readLocal(): Saved | null {
  try {
    const raw = JSON.parse(window.localStorage.getItem(KEY) ?? "null") as Saved | null
    return raw && Array.isArray(raw.paths) ? { paths: cleanFavourites(raw.paths), at: Number(raw.at) || 0 } : null
  } catch {
    return null
  }
}

function writeLocal(saved: Saved) {
  try {
    window.localStorage.setItem(KEY, JSON.stringify(saved))
  } catch {
    // private mode: the account still keeps them
  }
}

function accountFavourites(user: HqUser | null): Saved | null {
  const metadata = user?.metadata ?? null
  if (!metadata || !Array.isArray(metadata.hq_favourites)) return null
  return { paths: cleanFavourites(metadata.hq_favourites), at: Number(metadata.hq_favourites_at) || 0 }
}

// One store for every component on the page (More's stars and Today's links agree at once).
let current: Saved = { paths: [], at: 0 }
let loaded = false
const listeners = new Set<() => void>()
const emit = () => listeners.forEach((listener) => listener())

function ensureLoaded() {
  if (loaded || typeof window === "undefined") return
  loaded = true
  current = readLocal() ?? current
}

function subscribe(listener: () => void) {
  ensureLoaded()
  listeners.add(listener)
  const onStorage = (event: StorageEvent) => {
    if (event.key !== KEY) return
    current = readLocal() ?? { paths: [], at: 0 }
    emit()
  }
  window.addEventListener("storage", onStorage)
  return () => {
    listeners.delete(listener)
    window.removeEventListener("storage", onStorage)
  }
}

const snapshot = () => {
  ensureLoaded()
  return current
}

function set(next: Saved) {
  current = next
  writeLocal(next)
  emit()
}

async function saveToAccount(user: HqUser, saved: Saved, queryClient: ReturnType<typeof useQueryClient>) {
  await saveMyMetadata(queryClient, user, { hq_favourites: saved.paths, hq_favourites_at: saved.at })
}

/** The starred sections and a toggle. The newer of the device's and the account's choice wins. */
export function useFavourites() {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const saved = useSyncExternalStore(subscribe, snapshot, () => current)
  const account = accountFavourites(user)
  const { entry } = useMyRole()

  useEffect(() => {
    if (!user) return
    // A role given in Settings → Team adds its sections to the person's own stars, once.
    if (entry && entry.at > Math.max(current.at, account?.at ?? 0)) {
      const base = account && account.at > current.at ? account.paths : current.paths
      const next = { paths: cleanFavourites([...base, ...ROLE_FAVOURITES[entry.role]]), at: entry.at }
      set(next)
      void saveToAccount(user, next, queryClient).catch(() => undefined)
    } else if (account && account.at > current.at) set(account)
    else if (current.at > (account?.at ?? 0)) void saveToAccount(user, current, queryClient).catch(() => undefined)
    // A new choice is a new time, on either side; nothing else needs a re-run.
  }, [user?.id, account?.at, entry?.at]) // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = useCallback(
    (path: string) => {
      if (!STARRABLE.has(path)) return
      const paths = current.paths.includes(path) ? current.paths.filter((p) => p !== path) : [...current.paths, path]
      const next = { paths, at: Date.now() }
      set(next)
      if (user) void saveToAccount(user, next, queryClient).catch(() => undefined)
    },
    [user, queryClient]
  )

  const items = saved.paths.map((path) => NAV.find((item) => item.to === path)).filter((item): item is (typeof NAV)[number] => !!item)
  return { paths: saved.paths, items, toggle, isFavourite: (path: string) => saved.paths.includes(path), canStar: (path: string) => STARRABLE.has(path) }
}
